import React, { useState } from 'react';
import { Building2, Users } from 'lucide-react';
import Organizations from './Organizations';
import TeamPendingInvitations from './TeamPendingInvitations';
import { useOrganizationsStore, canSeeOrganizations } from '../store/organizationsStore';
import { setTeams } from '../store/teamsStore';
import teamsService from '../services/teamsService';
import { useAuthStore } from '../store/authStore';
import './Organizations.css';

/**
 * The Teams tab hosts teams, and — for organisations customers — the level
 * above them.
 *
 * Not a new activity-bar entry, deliberately (plan §5): an organisation is
 * context for teams, not a parallel workspace concept. Someone looking for
 * "where do I manage who can see what" should find one place, not two that
 * each hold half the answer.
 *
 * `teamsView` arrives already rendered, matching how AppContent takes its
 * views — Teams needs about fifteen props from App.js and threading them
 * through here would make this component a second place they can go wrong.
 *
 * The switcher shows for everyone, including users with no organisation:
 * hiding it would make creating a first one undiscoverable, which is the
 * failure that prompted this component. Organizations.js handles the empty
 * state with a create button.
 */
export default function TeamsArea({ teamsView }) {
  const [view, setView] = useState('teams');
  const organizations = useOrganizationsStore((s) => s.organizations);
  const loaded = useOrganizationsStore((s) => s.loaded);
  const plan = useAuthStore((s) => s.user?.plan);

  // Enterprise, or already in an organisation. Everyone else gets Teams with
  // no switcher and no hint that another level exists — a solo user should not
  // have to understand organisations to find their team.
  //
  // Gated on `loaded` as well: before the list arrives the answer is "not yet",
  // and showing the switcher then would make it appear a moment after the page
  // does, which reads as a glitch.
  const showOrganizations = loaded && canSeeOrganizations(plan, organizations);

  // Reload the shared teams list after accepting, so the team someone has just
  // joined is there rather than one refresh away. Same call the bootstrap
  // makes; the store has no loader of its own to reuse.
  const reloadTeams = async () => {
    try {
      const rows = await teamsService.getTeams();
      setTeams(Array.isArray(rows) ? rows : rows?.data ?? []);
    } catch {
      // The accept succeeded either way. The Teams page fetches on mount, so a
      // failure here costs a stale list until then, not the membership.
    }
  };

  if (!showOrganizations) {
    return (
      <>
        <TeamPendingInvitations onAccepted={reloadTeams} />
        {teamsView}
      </>
    );
  }

  return (
    <div className="teams-area">
      {/* A segmented control, not underlined tabs. Two peer views of one
          thing read as a switch; underlines read as sections of a page you
          are already inside.

          The rail exists so the control lines up with the CONTENT rather than
          with the window: both pages centre a 900px column with 24px of
          padding, and a switch centred on the viewport would sit off the axis
          everything below it shares. */}
      <div className="teams-area-rail">
      <div className="teams-area-switch" role="tablist" aria-label="Teams or organizations">
        <button
          type="button"
          role="tab"
          aria-selected={view === 'teams'}
          className={`teams-area-tab${view === 'teams' ? ' teams-area-tab--on' : ''}`}
          onClick={() => setView('teams')}
        >
          <Users size={13} aria-hidden /> Teams
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'orgs'}
          className={`teams-area-tab${view === 'orgs' ? ' teams-area-tab--on' : ''}`}
          onClick={() => setView('orgs')}
        >
          <Building2 size={13} aria-hidden /> Organizations
          {organizations.length > 0 && (
            <span className="teams-area-count">{organizations.length}</span>
          )}
        </button>
      </div>
      </div>

      <div className="teams-area-body">
        {/* Teams stays MOUNTED while organisations are shown: it holds
            scroll position, an open team detail and any in-flight fetch, and
            remounting it on every switch would throw all three away. */}
        <div style={{ display: view === 'teams' ? 'contents' : 'none' }}>
          <TeamPendingInvitations onAccepted={reloadTeams} />
          {teamsView}
        </div>
        {view === 'orgs' && <Organizations />}
      </div>
    </div>
  );
}
